<template>
  <div class="analyze-page">
    <header class="header-bar">
      <div class="logo">
        <el-icon class="logo-icon" size="20">
          <Histogram />
        </el-icon>
        <span class="logo-title">用户行为分析平台</span>
      </div>

      <div class="actions">
        <el-button plain type="primary" @click="refreshData" :loading="loading" class="action-btn">
          <template #icon>
            <Refresh />
          </template>
          刷新数据
        </el-button>
      </div>
    </header>

    <!-- 筛选条件 -->
    <div class="filter-bar">
      <el-form :inline="true" class="inline-form">
        <el-form-item style="font-size: large"><span>确定非活跃用户的筛选条件 :</span></el-form-item>
        <el-form-item label="登录次数 :">
          <el-input-number v-model="loginThreshold" :min="0" :step="1" />
        </el-form-item>
        <el-form-item label="下单次数 :">
          <el-input-number v-model="orderThreshold" :min="0" :step="1" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="applyFilter" :loading="loading">应用筛选</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 固定样式的统计卡片 -->
    <section class="stats-container">
      <!-- 总用户数 -->
      <div class="stat-card">
        <div class="stat-info">
          <p class="stat-title">总用户数</p>
          <p class="stat-value">{{ statsData.totalUsers }}</p>
          <p :class="['stat-change', monthlyComparison.totalUsersDirection]">
            <el-icon size="14">
              <CaretTop v-if="monthlyComparison.totalUsersDirection === 'up'" />
              <CaretBottom v-else />
            </el-icon>
            {{ monthlyComparison.totalUsersChange }}%
          </p>
          <p class="stat-sub">较上月</p>
        </div>
        <div class="stat-icon" style="background-color: rgba(76, 140, 255, 0.15)">
          <el-icon :size="22" color="#4C8CFF">
            <UserFilled />
          </el-icon>
        </div>
      </div>

      <!-- 活跃用户 -->
      <div class="stat-card">
        <div class="stat-info">
          <p class="stat-title">活跃用户</p>
          <p class="stat-value">{{ statsData.activeUsers }}</p>
          <p :class="['stat-change', monthlyComparison.activeUsersDirection]">
            <el-icon size="14">
              <CaretTop v-if="monthlyComparison.activeUsersDirection === 'up'" />
              <CaretBottom v-else />
            </el-icon>
            {{ monthlyComparison.activeUsersChange }}%
          </p>
          <p class="stat-sub">较上月</p>
        </div>
        <div class="stat-icon" style="background-color: rgba(42, 202, 179, 0.15)">
          <el-icon :size="22" color="#2ACAB3">
            <User />
          </el-icon>
        </div>
      </div>

      <!-- 非活跃用户 -->
      <div class="stat-card">
        <div class="stat-info">
          <p class="stat-title">非活跃用户</p>
          <p class="stat-value">{{ statsData.inactiveUsers }}</p>
          <p :class="['stat-change', monthlyComparison.inactiveUsersDirection]">
            <el-icon size="14">
              <CaretTop v-if="monthlyComparison.inactiveUsersDirection === 'up'" />
              <CaretBottom v-else />
            </el-icon>
            {{ monthlyComparison.inactiveUsersChange }}%
          </p>
          <p class="stat-sub">较上月</p>
        </div>
        <div class="stat-icon" style="background-color: rgba(255, 159, 67, 0.15)">
          <el-icon :size="22" color="#FF9F43">
            <WarningFilled />
          </el-icon>
        </div>
      </div>

      <!-- 总下单次数 -->
      <div class="stat-card">
        <div class="stat-info">
          <p class="stat-title">总下单次数</p>
          <p class="stat-value">{{ statsData.totalOrders }}</p>
          <p :class="['stat-change', monthlyComparison.totalOrdersDirection]">
            <el-icon size="14">
              <CaretTop v-if="monthlyComparison.totalOrdersDirection === 'up'" />
              <CaretBottom v-else />
            </el-icon>
            {{ monthlyComparison.totalOrdersChange }}%
          </p>
          <p class="stat-sub">较上月</p>
        </div>
        <div class="stat-icon" style="background-color: rgba(255, 114, 114, 0.15)">
          <el-icon :size="22" color="#FF7272">
            <ShoppingCartFull />
          </el-icon>
        </div>
      </div>
    </section>

    <!-- 分析视图选择 -->
    <section class="view-select-bar">
      <h3 class="view-select-title">分析视图选择</h3>
      <div class="view-btn-group">
        <el-button
            v-for="(view, idx) in views"
            :key="idx"
            plain
            type="primary"
            :class="['view-btn', { active: selectedView === idx }]"
            @click="selectView(idx)"
        >
          <template #icon>
            <component :is="view.icon" />
          </template>
          {{ view.label }}
        </el-button>
      </div>
    </section>

    <section class="chart-container">
      <!-- 图表区域 -->
      <!-- 当月用户行为分布散点图 -->
<!--      <ScatterChart-->
<!--          v-if="selectedView === 0"-->
<!--          ref="scatterChartRef"-->
<!--          :data="scatterData"-->
<!--          :problem-ids="inactiveIds"-->
<!--          title="当月用户行为分布散点图"-->
<!--      />-->

      <!-- 双指标柱状图对比 -->
      <BarChart
          v-if="selectedView === 0"
          ref="barChartRef"
          :data="barData"
          title="登陆次数和下单次数的总计对比"
      />

      <!-- 单用户活跃度趋势 -->
      <TrendChart
          v-if="selectedView === 1"
          ref="trendChartRef"
          :userData="selectedUserData"
          title="用户下单、登陆次数的趋势分析"
          @user-change="handleUserChange"
      />

      <!-- 当月用户行为数据表和问题分析 -->
      <DataTable
          v-if="selectedView === 2"
          ref="dataTableRef"
          :table-data="userTableData"
          :login-threshold="loginThreshold"
          :order-threshold="orderThreshold"
          @problem-analyzed="handleProblemAnalyzed"
      />
    </section>
  </div>
</template>

<script setup>
import {
  Histogram,
  Refresh,
  // PieChart,
  Clock,
  Grid,
  UserFilled,
  User,
  WarningFilled,
  ShoppingCartFull,
  CaretTop,
  CaretBottom
} from '@element-plus/icons-vue'
import { ref, reactive, onMounted, markRaw } from 'vue'
import { ElMessage } from 'element-plus'

// 引入图表组件
// import ScatterChart from '@/components/analyze/ScatterChart.vue'
import BarChart from '@/components/analyze/BarChart.vue'
import TrendChart from '@/components/analyze/TrendChart.vue'
import DataTable from '@/components/analyze/DataTable.vue'

// 引入API服务
import { getUserAnalyzeStats } from '@/services/userAnalyzeService.js'

// 统计数据和月度对比数据
const statsData = reactive({
  totalUsers: 0,    // 总用户数
  activeUsers: 0,   // 活跃用户
  inactiveUsers: 0, // 非活跃用户
  totalOrders: 0    // 总下单次数
})

// 月度对比数据
const monthlyComparison = reactive({
  totalUsersChange: 0,
  activeUsersChange: 0,
  inactiveUsersChange: 0,
  totalOrdersChange: 0,
  totalUsersDirection: 'up',
  activeUsersDirection: 'up',
  inactiveUsersDirection: 'down',
  totalOrdersDirection: 'up'
})

// 用于高亮散点图中的问题用户
const inactiveIds = ref([])

// 筛选条件
const loginThreshold = ref(1)
const orderThreshold = ref(1)

// 加载状态
const loading = ref(false)

const applyFilter = async () => {
  if (loading.value) return
  // console.log('应用筛选', loginThreshold.value, orderThreshold.value)
  await fetchUserAnalyzeStats()
}

// 分析视图
const views = ref([
  // { label: '当月用户行为分布散点图', icon: markRaw(PieChart) },
  { label: '登陆次数和下单次数的总计对比', icon: markRaw(Histogram) },
  { label: '用户下单、登陆次数的趋势分析', icon: markRaw(Clock) },
  { label: '当月用户行为数据表和问题分析', icon: markRaw(Grid) }
])

const selectedView = ref(2) // 默认第二项

const selectView = idx => {
  selectedView.value = idx

  // 使用v-if后，组件会在切换时重新创建，需要更长的延迟等待组件挂载完成
  setTimeout(() => {
    if (idx === 0 && barChartRef.value) {
      barChartRef.value.resizeChart()
    } else if (idx === 1 && trendChartRef.value) {
      trendChartRef.value.resizeChart()
    }
  }, 300) // 增加延迟到300ms，确保组件完全挂载
}

// ---------- 图表数据 ----------
const barChartRef = ref(null)
const trendChartRef = ref(null)
const dataTableRef = ref(null)

// // 散点图数据
// const scatterData = ref([])

// 柱状图数据
const barData = ref({
  categories: [],
  loginData: [],
  orderData: []
})

// 选中的用户数据
const selectedUserData = ref(null)

// 处理用户变更事件
const handleUserChange = (userData) => {
  selectedUserData.value = userData
}

// 数据表格相关
const userTableData = ref([])

// 调用后端API获取用户分析统计数据
const fetchUserAnalyzeStats = async () => {
  loading.value = true
  try {
    // 获取统计月份（当前月份第一天）
    const statMonth = new Date().toISOString().substring(0, 7) + '-01'
    
    // 调用API服务
    const result = await getUserAnalyzeStats(statMonth, loginThreshold.value, orderThreshold.value)
    
    if (result.success && result.data) {
      const data = result.data
      
      // 更新统计数据
      statsData.totalUsers = data.totalUsers || 0
      statsData.activeUsers = data.activeUsers || 0
      statsData.inactiveUsers = data.inactiveUsers || 0
      statsData.totalOrders = data.totalOrders || 0
      
      // 更新月度对比数据
      if (data.monthlyComparison) {
        monthlyComparison.totalUsersChange = data.monthlyComparison.totalUsersChange || 0
        monthlyComparison.activeUsersChange = data.monthlyComparison.activeUsersChange || 0
        monthlyComparison.inactiveUsersChange = data.monthlyComparison.inactiveUsersChange || 0
        monthlyComparison.totalOrdersChange = data.monthlyComparison.totalOrdersChange || 0
        monthlyComparison.totalUsersDirection = data.monthlyComparison.totalUsersDirection || 'up'
        monthlyComparison.activeUsersDirection = data.monthlyComparison.activeUsersDirection || 'up'
        monthlyComparison.inactiveUsersDirection = data.monthlyComparison.inactiveUsersDirection || 'down'
        monthlyComparison.totalOrdersDirection = data.monthlyComparison.totalOrdersDirection || 'up'
      }
      
      console.log('用户分析统计数据获取成功:', data)
      
      // 使用后端返回的真实数据
      // if (data.scatterData) {
      //   scatterData.value = data.scatterData
      // }
      
      if (data.barChartData) {
        barData.value = {
          categories: data.barChartData.categories || [],
          loginData: data.barChartData.loginData || [],
          orderData: data.barChartData.orderData || []
        }
      }
      
      if (data.userTableData) {
        userTableData.value = data.userTableData
      }
      
      // 根据返回的表格数据重新计算非活跃用户名列表，确保与散点图第三列一致
      inactiveIds.value = (data.userTableData || [])
        .filter(u => u.status === '非活跃')
        .map(u => u.username)
      
      ElMessage.success('数据更新成功')
    } else {
      throw new Error(result.error || '获取统计数据失败')
    }
  } catch (error) {
    console.error('获取用户分析统计数据失败:', error)
    ElMessage.error(`数据获取失败: ${error.message}`)
    
    // 设置默认值
    statsData.totalUsers = 0
    statsData.activeUsers = 0
    statsData.inactiveUsers = 0
    statsData.totalOrders = 0
    inactiveIds.value = []
    
    // 重置月度对比数据
    monthlyComparison.totalUsersChange = 0
    monthlyComparison.activeUsersChange = 0
    monthlyComparison.inactiveUsersChange = 0
    monthlyComparison.totalOrdersChange = 0
    monthlyComparison.totalUsersDirection = 'up'
    monthlyComparison.activeUsersDirection = 'up'
    monthlyComparison.inactiveUsersDirection = 'down'
    monthlyComparison.totalOrdersDirection = 'up'
    
    // 清空图表数据
    // scatterData.value = []
    barData.value = { categories: [], loginData: [], orderData: [] }
    userTableData.value = []
  } finally {
    loading.value = false
  }
}

// 虚拟数据生成函数已删除，现在使用后端返回的真实数据

// 处理问题分析结果
const handleProblemAnalyzed = (result) => {
  console.log('问题分析结果:', result)
  // 可以在这里处理分析结果，比如更新统计卡片数据
}

// 刷新数据
const refreshData = async () => {
  if (loading.value) return
  // console.log('刷新数据')
  await fetchUserAnalyzeStats()
}



// 页面挂载时自动获取数据
onMounted(() => {
  fetchUserAnalyzeStats()
})
</script>

<style scoped>
.analyze-page {
  width: 100%;
  min-height: 100%;
  background-color: #f5f7fa;
}

.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #ffffff;
}

.logo {
  display: flex;
  align-items: center;
  font-size: 20px;
  color: #0066ff;
  font-weight: 600;
}

.logo-icon {
  margin-right: 6px;
}

.actions {
  display: flex;
  align-items: center;
}

.action-btn:not(:last-child) {
  margin-right: 16px;
}

.export-btn {
  /* 按钮宽度固定，避免文字变动导致抖动 */
  min-width: 100px;
}



/* 统计卡片区域 */
.stats-container {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.stat-card {
  flex: 1 1 calc(20%);
  min-width: 220px;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  margin-top: 10px;
}

.stat-title {
  margin: 0 0 8px;
  font-size: 14px;
  color: #606266;
}

.stat-value {
  margin: 0 0 12px;
  font-size: 24px;
  font-weight: 600;
  color: #1f2d3d;
}

.stat-change {
  margin: 0;
  font-size: 12px;
  display: flex;
  align-items: center;
}

.stat-change.up {
  color: #10c469;
}

.stat-change.down {
  color: #ff5b5b;
}

.stat-change .el-icon {
  margin-right: 2px;
}

.stat-sub {
  margin: 4px 0 0;
  font-size: 12px;
  color: #909399;
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 筛选栏 */
.filter-bar {
  background-color: #ffffff;
  padding: 16px;
  border-bottom: 1px solid #ebeef5;
}

/* 取消表单的最大宽度限制 */
.el-form.inline-form {
  max-width: none !important;
  width: 100% !important;
}

/* 响应式设计 - 830px断点 */
@media screen and (max-width: 830px) {
  .filter-bar {
    text-align: center;
  }
  
  .filter-bar .el-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
  
  .filter-bar .el-form-item {
    margin-bottom: 0;
    margin-right: 0;
  }
  
  .filter-bar .el-form-item:first-child {
    margin-bottom: 16px;
  }
  
  .filter-bar .el-button {
    min-width: 120px;
  }
}

/* 移动端响应式设计 - 768px断点 */
@media screen and (max-width: 768px) {
  .header-bar {
    padding: 10px 16px;
  }
  
  .logo-title {
    font-size: 16px;
  }
  
  .action-btn {
    font-size: 14px;
    padding: 8px 12px;
  }
  
  /* 统计卡片移动端优化 */
  .stats-container {
    gap: 12px;
  }
  
  .stat-card {
    min-width: calc(50% - 6px);
    padding: 16px;
  }
  
  .stat-title {
    font-size: 12px;
  }
  
  .stat-value {
    font-size: 20px;
    margin-bottom: 8px;
  }
  
  .stat-change {
    font-size: 11px;
  }
  
  .stat-sub {
    font-size: 11px;
  }
  
  .stat-icon {
    width: 32px;
    height: 32px;
  }
  
  .stat-icon .el-icon {
    font-size: 18px;
  }
  
  /* 视图选择移动端优化 */
  .view-select-bar {
    padding: 16px;
  }
  
  .view-select-title {
    font-size: 16px;
    margin-bottom: 12px;
  }
  
  .view-btn-group {
    gap: 8px;
  }
  
  .view-btn {
    flex: 1 1 calc(50% - 4px);
    min-width: calc(50% - 4px);
    font-size: 13px;
    padding: 8px 12px;
  }
  
  /* 图表容器移动端优化 */
  .chart-container {
    padding: 12px;
  }
}

/* 超小屏幕优化 - 480px断点 */
@media screen and (max-width: 480px) {
  .logo-title {
    font-size: 14px;
  }
  

  
  .view-btn {
    flex: 1 1 100%;
    min-width: 100%;
  }
}

/* 视图选择区域 */
.view-select-bar {
  background-color: #ffffff;
  margin-top: 16px;
  padding: 24px;
  border-radius: 8px;
}

.view-select-title {
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.view-btn-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.view-btn {
  border-color: #0066ff;
  color: #0066ff;
}

.view-btn.active {
  background-color: #0066ff !important;
  color: #ffffff !important;
}

.view-btn.active:hover,
.view-btn.active:focus {
  background-color: #0066ff !important;
  color: #ffffff !important;
}

/* 图表 */
.chart-container {
  background-color: #ffffff;
  margin: 10px;
  border-radius: 8px;
  padding: 16px;
}
.el-button+.el-button {
  margin-left: 0 !important;
}



</style>